import React from "react";
import { Alert, Button } from "@mui/material";
import {
  ATTESTATION_BLOCKED_REASONS,
  AttestationStatus,
} from "../../../types/attestation";
import {
  canScopeToPayPeriod,
  daysInCurrentPayPeriod,
} from "../../../components/forms/providerAppointmentForms/attestation.helper";

interface IProps {
  attestationStatus: AttestationStatus | null;
  onReviewAndAttest: (date: string) => void;
  /**
   * Opens a blocked day so the BT can deal with whatever is holding it up. Separate from
   * `onReviewAndAttest` because such a day cannot be attested yet, so it must not start a
   * catch-up run — the BT is going there to fill something in, not to confirm.
   */
  onAddMileage: (date: string) => void;
}

/** Formats a YYYY-MM-DD period end for display, or returns null if it is not a usable date. */
const formatPeriodEnd = (endDate: string): string | null => {
  const [year, month, day] = endDate.split("-").map(Number);

  if (!year || !month || !day) {
    return null;
  }
  const parsed = new Date(Date.UTC(year, month - 1, day));

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

/**
 * Tells a BT where they stand on attestation for the current pay period, and always gives them
 * somewhere to go. Renders nothing when there is nothing owed. When every remaining day is
 * blocked on missing mileage the call to action changes to point at the mileage instead —
 * naming a problem without offering a route to it would leave the BT hunting for the day.
 */
const AttestationBanner: React.FC<IProps> = ({
  attestationStatus,
  onReviewAndAttest,
  onAddMileage,
}) => {
  /*
   * Derived from `days` rather than trusting the counts, so a partial or inconsistent response
   * renders nothing instead of throwing inside the schedule page.
   *
   * Scoped to the current pay period, because `days` also covers the week on screen. This
   * message is about one deadline: a day from a closed period, or one the BT navigated to, keeps
   * its marker on the strip and stays confirmable, but counting it here would demand it be
   * cleared by a date it was never subject to.
   */
  const payPeriodEnd = attestationStatus?.payPeriod?.endDate;
  const days = daysInCurrentPayPeriod(attestationStatus);

  if (!canScopeToPayPeriod(attestationStatus)) {
    return null;
  }

  /*
   * Sorted here rather than trusting the response. "Oldest day still owed" is what the call to
   * action promises, and the ordering that backs it is decided in a different service — cheap
   * to guarantee locally, and a silently reordered payload would send the BT to the wrong day
   * with nothing on screen looking wrong.
   */
  const unattestedDays = days
    .filter((day) => !day.isAttested)
    .sort((a, b) => a.date.localeCompare(b.date));
  const unattestedCount = unattestedDays.length;

  if (unattestedCount === 0) {
    return null;
  }

  const dayLabel = unattestedCount === 1 ? "day" : "days";
  const dayVerb = unattestedCount === 1 ? "needs" : "need";
  /*
   * The oldest day still owed, blocked or not. Blocked days used to be skipped, back when
   * landing on one meant a disabled button and no explanation — but a blocked day now shows why
   * it is stuck and can be fixed in place, so skipping it steered the BT away from exactly the
   * days needing the most work and left them owed indefinitely.
   */
  const oldestOutstandingDay = unattestedDays[0];
  const everyRemainingDayIsBlocked = unattestedDays.every((day) => day.isBlocked);
  // Absent as well as malformed: the period *start* is what has to be known here, so a response
  // missing only the end still shows the count and drops the deadline sentence.
  const formattedPeriodEnd = payPeriodEnd ? formatPeriodEnd(payPeriodEnd) : null;

  /*
   * Box treatment matches the availability notice this sits above — same radius and spacing —
   * so the two read as siblings in the page. The palette stays red on purpose: a pay-period
   * deadline should not look like the same class of message as an FYI.
   *
   * The message area is a row: the two lines of text on the left, the action on the right.
   * `gap` rather than a margin so the button never touches the text when the copy is long.
   */
  const sharedSx = {
    mb: 2,
    backgroundColor: "#FDECEA",
    color: "#611A15",
    borderRadius: "8px",
    "& .MuiAlert-icon": {
      color: "#B60000",
    },
    "& .MuiAlert-message": {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 2,
      width: "100%",
    },
  };

  if (everyRemainingDayIsBlocked) {
    const oldestBlockedDay = unattestedDays[0];
    /*
     * Only claim mileage when that is what the server actually said. A reason this build does
     * not recognise means the backend is ahead of the frontend; naming the wrong cause would
     * send the BT hunting for mileage that is already there.
     */
    const everyBlockerIsMileage = unattestedDays.every(
      (day) => day.blockedReason === ATTESTATION_BLOCKED_REASONS.missingMileage
    );

    return (
      <Alert severity="warning" sx={sharedSx} data-testid="attestation-banner">
        <div className="attestation-banner-text">
          <span className="attestation-banner-title">
            {unattestedCount} {dayLabel} {dayVerb} attention
          </span>
          <span className="attestation-banner-detail">
            {everyBlockerIsMileage ? (
              <>
                Mileage is missing on{" "}
                {unattestedCount === 1 ? "this day" : "these days"}, so{" "}
                {unattestedCount === 1 ? "it" : "they"} can&apos;t be attested
                yet. Add the missing mileage, then confirm the {dayLabel}.
              </>
            ) : (
              <>
                {unattestedCount === 1 ? "This day" : "These days"} can&apos;t be
                attested yet. Open the {dayLabel} to see what is outstanding.
              </>
            )}
          </span>
        </div>
        <div className="attestation-banner-action">
          <Button
            variant="contained"
            data-testid="attestation-banner-mileage-cta"
            onClick={() => onAddMileage(oldestBlockedDay.date)}
          >
            {everyBlockerIsMileage ? "Add mileage" : "Open day"}
          </Button>
        </div>
      </Alert>
    );
  }

  return (
    <Alert severity="warning" sx={sharedSx} data-testid="attestation-banner">
      <div className="attestation-banner-text">
        <span className="attestation-banner-title">
          {unattestedCount} unattested {dayLabel} this pay period
        </span>
        <span className="attestation-banner-detail">
          {formattedPeriodEnd
            ? `Attest all days before the pay period ends on ${formattedPeriodEnd}.`
            : "Attest all days before this pay period ends."}
        </span>
      </div>
      <div className="attestation-banner-action">
        <Button
          variant="contained"
          data-testid="attestation-banner-cta"
          onClick={() => onReviewAndAttest(oldestOutstandingDay.date)}
        >
          Review &amp; attest
        </Button>
      </div>
    </Alert>
  );
};

export default AttestationBanner;
